/**
 * Association matching helpers.
 *
 * Associations in the CRM schema match endpoints in one of two ways:
 *
 *  - **Type-based** (`source_entity_type_name` / `target_entity_type_name`):
 *    a single concrete entity type. Cheap equality check.
 *  - **Kind-based** (`source_entity_kind_name` / `target_entity_kind_name`):
 *    every entity type whose `entity_kind` tuple contains the named kind,
 *    minus anything in the corresponding exclusion list. The special kind
 *    `'any'` covers every entity type (minus exclusions).
 *
 * The frontend has historically only matched by type-name equality:
 *
 *   associationTypes.some(at =>
 *     at.source_entity_type_name === 'ahoy_note' &&
 *     at.target_entity_type_name === object.entity_type_name
 *   )
 *
 * That silently misses every kind-based association. `ahoy_note_to_contact`
 * is kind-based on `person`, so the notes section never rendered in the
 * contact slideout — and similarly for files, research, meetings, etc.
 *
 * Mirrors `AssociationType.matches_source` / `matches_target` in
 * `apps/crm_events/schema_registry/types.py`. Keep these in sync.
 */

import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '../core/SchemaClient';

/** Special kind name that matches every entity type. */
const KIND_ANY = 'any';

/**
 * Match an entity type by its entity_kind tuple against a kind-based endpoint.
 * Returns false if the kind doesn't match OR the entity type is in the exclusion list.
 */
function kindMatches(
  requiredKind: string,
  entityTypeName: string,
  entityKinds: readonly string[],
  excluded: readonly string[]
): boolean {
  if (excluded.includes(entityTypeName)) {
    return false;
  }
  if (requiredKind === KIND_ANY) {
    return true;
  }
  return entityKinds.includes(requiredKind);
}

/**
 * Does the association's source side match the given entity type?
 *
 * Handles both type-based (direct equality on `source_entity_type_name`) and
 * kind-based matching (`source_entity_kind_name` + `source_excluded_entity_type_names`).
 *
 * @param at - the association type definition
 * @param entityTypeName - the entity type to check (e.g. 'ahoy_contact')
 * @param entityKinds - the entity_kind tuple for that type (from `CrmObjectTypeSchema.entity_kind`)
 */
export function associationMatchesSource(
  at: AssociationType,
  entityTypeName: string,
  entityKinds: readonly string[]
): boolean {
  if (at.source_entity_type_name) {
    return at.source_entity_type_name === entityTypeName;
  }
  if (at.source_entity_kind_name) {
    return kindMatches(
      at.source_entity_kind_name,
      entityTypeName,
      entityKinds,
      at.source_excluded_entity_type_names ?? []
    );
  }
  // Schema invariant: every association must have either a type-based or
  // kind-based source. If neither is set, the schema is malformed — treat as
  // no match rather than throwing, since this code path runs during render.
  return false;
}

/**
 * Does the association's target side match the given entity type?
 *
 * See `associationMatchesSource` — same logic for the target side.
 */
export function associationMatchesTarget(
  at: AssociationType,
  entityTypeName: string,
  entityKinds: readonly string[]
): boolean {
  if (at.target_entity_type_name) {
    return at.target_entity_type_name === entityTypeName;
  }
  if (at.target_entity_kind_name) {
    return kindMatches(
      at.target_entity_kind_name,
      entityTypeName,
      entityKinds,
      at.target_excluded_entity_type_names ?? []
    );
  }
  return false;
}

/**
 * Build a Map of `entity_type_name → entity_kind[]` from object type schemas.
 * Use this to feed entityKinds into the matcher functions.
 */
export function buildEntityKindMap(
  objectTypes: readonly CrmObjectTypeSchema[]
): ReadonlyMap<string, readonly string[]> {
  const map = new Map<string, readonly string[]>();
  for (const ot of objectTypes) {
    map.set(ot.entity_type_name, ot.entity_kind);
  }
  return map;
}

/**
 * Convenience: look up entity_kind tuple for a type, returning [] if unknown.
 * Unknown types degrade to "type-based matching only" — which is safe behavior
 * (won't match kind-based associations, but won't false-positive either).
 */
export function getEntityKinds(
  entityTypeName: string,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): readonly string[] {
  return entityKindMap.get(entityTypeName) ?? [];
}

/**
 * Resolve the entity kinds of an association's COUNTERPART, relative to a given
 * entity type. The counterpart is the side that is *not* `entityType`:
 *  - if `entityType` matches the association's source, the counterpart is the target;
 *  - otherwise the counterpart is the source.
 *
 * Handles both type-based counterparts (resolve `entity_kind[]` from the type name
 * via the kind map) and kind-based counterparts (the single `*_entity_kind_name`
 * is itself the kind). Returns `[]` when the counterpart can't be resolved.
 *
 * Used by the create form to decide which association fields belong to a custom
 * entity type's "domain" (vs. system/utility associations like Reminders).
 */
export function counterpartEntityKinds(
  at: AssociationType,
  entityType: string,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): readonly string[] {
  const entityIsSource = associationMatchesSource(
    at,
    entityType,
    getEntityKinds(entityType, entityKindMap)
  );
  const counterpartTypeName = entityIsSource
    ? at.target_entity_type_name
    : at.source_entity_type_name;
  const counterpartKindName = entityIsSource
    ? at.target_entity_kind_name
    : at.source_entity_kind_name;
  if (counterpartTypeName) {
    return getEntityKinds(counterpartTypeName, entityKindMap);
  }
  if (counterpartKindName) {
    return [counterpartKindName];
  }
  return [];
}

/** Minimal shape of an entity-type schema needed for target-type resolution. */
export interface EntityTypeSchemaLike {
  entity_type_name: string;
  entity_kind?: string[];
}

/** Minimal association target fields both resolvers accept. */
type AssociationTargetLike = Pick<
  AssociationType,
  | 'target_entity_type_name'
  | 'target_entity_kind_name'
  | 'target_excluded_entity_type_names'
>;

/**
 * Resolve ALL concrete target entity type names an association can point at.
 * Type-based associations expose exactly one `target_entity_type_name`;
 * kind-based associations resolve to every matching object type, excluding the
 * schema's excluded types. Returns [] when nothing resolves yet (e.g. a
 * kind-based association whose objectTypes are still loading).
 *
 * Use this (not the singular `resolveAssociationTargetType`) whenever the number
 * of matching target types is itself significant — e.g. deciding whether a
 * property is declared on a UNIQUE target, which is the backend's sortability
 * gate for compound lookup sorts.
 */
export function resolveAssociationTargetTypes(
  associationType: AssociationTargetLike | undefined,
  objectTypes: EntityTypeSchemaLike[] | undefined
): string[] {
  if (!associationType) {
    return [];
  }
  const directType = associationType.target_entity_type_name;
  if (directType) {
    return [directType];
  }
  const kindName = associationType.target_entity_kind_name;
  if (!kindName || !objectTypes) {
    return [];
  }
  const excluded = new Set(
    associationType.target_excluded_entity_type_names ?? []
  );
  // Mirror kindMatches: the special kind `any` matches every entity type
  // (minus exclusions), so a wildcard target must enumerate all object types
  // rather than resolving to nothing.
  return objectTypes
    .filter(
      t =>
        (kindName === KIND_ANY || t.entity_kind?.includes(kindName)) &&
        !excluded.has(t.entity_type_name)
    )
    .map(t => t.entity_type_name);
}

/**
 * Resolve the concrete target entity type name for an association so callers
 * can fetch that type's properties. Type-based associations expose
 * `target_entity_type_name` directly; kind-based associations resolve to the
 * first matching object type, excluding the schema's excluded types.
 * Returns '' when it can't be resolved yet (e.g. objectTypes still loading).
 */
export function resolveAssociationTargetType(
  associationType: AssociationTargetLike | undefined,
  objectTypes: EntityTypeSchemaLike[] | undefined
): string {
  return resolveAssociationTargetTypes(associationType, objectTypes)[0] ?? '';
}

/** Target entity kinds whose associations are activity-like noise and stay hidden on the overview. */
const HIDDEN_TARGET_KINDS: ReadonlySet<string> = new Set([
  'activity',
  'event',
  'reporting',
]);

/**
 * Is an association visible on the record overview, based on its TARGET's kind?
 * Denylist semantics: visible UNLESS the target resolves to an activity-like kind.
 * Type-based target: resolve kinds via the map (empty map -> [] -> visible, fail open).
 * Kind-based target: the single kind name is consulted directly (no map needed),
 * so a kind-based hidden target stays hidden even when the map is empty (fail closed).
 */
export function isAssociationVisibleOnOverview(
  at: AssociationType,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): boolean {
  let kinds: readonly string[];
  if (at.target_entity_type_name) {
    kinds = getEntityKinds(at.target_entity_type_name, entityKindMap);
  } else if (at.target_entity_kind_name) {
    kinds = [at.target_entity_kind_name];
  } else {
    kinds = [];
  }
  return !kinds.some(k => HIDDEN_TARGET_KINDS.has(k));
}
